/**
 * Students needing most attention.
 *
 * The one teacher surface that is not about a single paper. Every other view
 * answers "how did this class do on Tuesday's test"; this answers "who has been
 * stuck on the same thing since April", which is the question a class teacher is
 * actually asked at a PTM and the one no single sheet can answer.
 *
 * A child appears here only when a topic came BACK — Orange or Pink on most of
 * a topic's questions, in four of every five papers that asked it. One bad chapter in one paper is a
 * question to re-teach, not a child to worry about, and a list that cannot tell
 * those apart is a list nobody reads twice.
 *
 * Names, not counts, and every name opens that child's own report — the same
 * report they are reading themselves.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { RepeatStruggler } from '@cwy/config/contracts';

/** How many of a child's recurring topics to name before counting the rest. */
const TOPICS_SHOWN = 3;

export function RepeatStrugglers({
  classroomId,
  onOpenStudent,
}: {
  /** The class whose papers are laid on top of each other. */
  classroomId: string;
  onOpenStudent: (studentId: string) => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['repeat-strugglers', classroomId],
    queryFn: () =>
      apiGet<RepeatStruggler[]>(
        `/teacher/repeat-strugglers?classroomId=${encodeURIComponent(classroomId)}`,
      ),
  });

  if (isLoading) {
    return (
      <div
        className="h-24 animate-pulse rounded-2xl"
        style={{ background: 'var(--surface-sunk)' }}
      />
    );
  }
  if (isError) return null;

  const rows = data ?? [];

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
    >
      <div
        className="px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--paper-sunk)' }}
      >
        <p className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          Students needing most attention
        </p>
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {rows.length
            ? 'Topics they get wrong almost every time they meet them. Open a name to read their sheet.'
            : 'Nobody is stuck on the same topic twice — everything here is a one-off.'}
        </p>
      </div>

      <ul>
        {rows.slice(0, 8).map((row) => (
          <li
            key={row.studentId}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <button
              type="button"
              onClick={() => onOpenStudent(row.studentId)}
              className="text-fs-13 font-bold underline-offset-2 hover:underline"
              style={{ color: 'var(--text-strong)' }}
            >
              {row.studentName}
            </button>

            <span data-numeric="" className="text-xs" style={{ color: 'var(--ink-3)' }}>
              Roll {row.rollNumber} · {row.averagePercent}% over {row.papersSat} papers
            </span>

            {/* Their worst three, not all of them. Eleven chips per child was a
                wall of orange that said "everything" and so said nothing; the
                rest are counted rather than named, and the sheet above is where
                you go for the full picture. */}
            <span className="flex w-full flex-wrap items-baseline gap-1.5">
              {row.repeatTopics.slice(0, TOPICS_SHOWN).map((topic) => (
                <span
                  key={topic.topicId}
                  className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                  style={{ background: 'var(--orange-fill)', color: 'var(--orange-deep)' }}
                  title={`Wrong in ${topic.papers} of the ${topic.opportunities} papers that asked it — ${topic.questions} questions in all`}
                >
                  {topic.topicName}
                  <span data-numeric="" className="font-bold">
                    {' '}
                    · {topic.papers} of {topic.opportunities}
                  </span>
                </span>
              ))}
              {row.repeatTopics.length > TOPICS_SHOWN && (
                <span className="text-fs-11" style={{ color: 'var(--ink-3)' }}>
                  +{row.repeatTopics.length - TOPICS_SHOWN} more
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
